import { onSnapshot, Query, QuerySnapshot, DocumentData, FirestoreError } from 'firebase/firestore';

/**
 * Megosztott Firestore figyelő.
 *
 * Több komponens ugyanarra a lekérdezésre (pl. „a cég összes ügyfele”) EGY onSnapshot-ot használ:
 * az első feliratkozó indítja, az utolsó leiratkozása után rövid ideig (keepAlive) még él,
 * hogy egy modal bezárás/nyitás ne olvassa újra az egész kollekciót. Az adat memóriában marad,
 * az új feliratkozó azonnal megkapja – a Firestore csak a változásokat küldi.
 */

interface Entry<T> {
  data: T[] | null;
  error: FirestoreError | null;
  listeners: Set<(data: T[] | null, error: FirestoreError | null) => void>;
  unsub: (() => void) | null;
  timer: ReturnType<typeof setTimeout> | null;
}

const entries = new Map<string, Entry<any>>();
const KEEP_ALIVE_MS = 60_000;

export function subscribeShared<T>(
  key: string,
  makeQuery: () => Query<DocumentData>,
  map: (snap: QuerySnapshot<DocumentData>) => T[],
  cb: (data: T[] | null, error: FirestoreError | null) => void
): () => void {
  let e = entries.get(key) as Entry<T> | undefined;
  if (!e) {
    e = { data: null, error: null, listeners: new Set(), unsub: null, timer: null };
    entries.set(key, e);
  }
  if (e.timer) { clearTimeout(e.timer); e.timer = null; }
  e.listeners.add(cb);
  if (!e.unsub) {
    const entry = e;
    try {
      entry.unsub = onSnapshot(makeQuery(), (snap) => {
        try { entry.data = map(snap); entry.error = null; } catch (err) { console.error('[sharedQuery] map hiba', key, err); entry.data = []; }
        entry.listeners.forEach(l => l(entry.data, null));
      }, (err) => {
        entry.error = err;
        console.error('[sharedQuery] figyelő hiba', key, err);
        entry.listeners.forEach(l => l(entry.data, err));
      });
    } catch (err) {
      console.error('[sharedQuery] lekérdezés hiba', key, err);
      cb(null, err as FirestoreError);
    }
  } else if (e.data !== null || e.error) {
    // már van adat: azonnal
    cb(e.data, e.error);
  }
  return () => {
    const entry = entries.get(key) as Entry<T> | undefined;
    if (!entry) return;
    entry.listeners.delete(cb);
    if (entry.listeners.size === 0 && !entry.timer) {
      entry.timer = setTimeout(() => {
        if (entry.listeners.size === 0) {
          try { entry.unsub?.(); } catch { /* ignore */ }
          entries.delete(key);
        }
      }, KEEP_ALIVE_MS);
    }
  };
}

/** Teszt / kilépés: minden megosztott figyelő leállítása (pl. user váltásnál). */
export function resetSharedQueries(): void {
  entries.forEach(e => { try { e.unsub?.(); } catch { /* ignore */ } if (e.timer) clearTimeout(e.timer); });
  entries.clear();
}
